import { defineDomainTranslations } from './defineDomainTranslations';

const en = {
  'records.credits.freeRecordingAvailable': '1 free recording · up to {minutes} min',
  'records.credits.freeRecordingUsed': 'Free recording used',
  'records.credits.unlimited': 'Unlimited',
  'records.newRecording': 'New recording',
  'records.pageTitle': 'Record videos',
  'records.preparingUpdatedVideo': 'Preparing the updated video…',
  'records.status.aborted': 'Aborted',
  'records.status.completed': 'Completed',
  'records.status.created': 'Created',
  'records.status.failed': 'Failed',
  'records.status.processing': 'Processing',
  'records.translate': 'Translate',
  'records.translateAria': 'Translate {name}',
  'records.type': 'Screen recording',
  'records.zeroState.action': 'Record video',
  'records.zeroState.description':
    'Just hit record and talk — we cut the filler, pauses and stumbles for you. One take is enough.',
  'records.zeroState.title': "Record. We'll clean it up.",
} as const;

type RecordsTranslation = Record<keyof typeof en, string>;

const es = {
  'records.credits.freeRecordingAvailable': '1 grabación gratuita · hasta {minutes} min',
  'records.credits.freeRecordingUsed': 'Grabación gratuita ya utilizada',
  'records.credits.unlimited': 'Ilimitado',
  'records.newRecording': 'Nueva grabación',
  'records.pageTitle': 'Grabar vídeos',
  'records.preparingUpdatedVideo': 'Preparando el vídeo actualizado…',
  'records.status.aborted': 'Interrumpida',
  'records.status.completed': 'Completada',
  'records.status.created': 'Creada',
  'records.status.failed': 'Fallida',
  'records.status.processing': 'Procesando',
  'records.translate': 'Traducir',
  'records.translateAria': 'Traducir {name}',
  'records.type': 'Grabación de pantalla',
  'records.zeroState.action': 'Grabar vídeo',
  'records.zeroState.description':
    'Solo pulsa grabar y habla: eliminamos las muletillas, las pausas y los titubeos por ti. Basta con una toma.',
  'records.zeroState.title': 'Graba. Nosotros lo pulimos.',
} satisfies RecordsTranslation;

const pt = {
  'records.credits.freeRecordingAvailable': '1 gravação gratuita · até {minutes} min',
  'records.credits.freeRecordingUsed': 'Gravação gratuita usada',
  'records.credits.unlimited': 'Ilimitado',
  'records.newRecording': 'Nova gravação',
  'records.pageTitle': 'Gravar vídeos',
  'records.preparingUpdatedVideo': 'Preparando o vídeo atualizado…',
  'records.status.aborted': 'Cancelada',
  'records.status.completed': 'Concluída',
  'records.status.created': 'Criada',
  'records.status.failed': 'Falhou',
  'records.status.processing': 'Processando',
  'records.translate': 'Traduzir',
  'records.translateAria': 'Traduzir {name}',
  'records.type': 'Gravação de tela',
  'records.zeroState.action': 'Gravar vídeo',
  'records.zeroState.description':
    'É só gravar e falar: cortamos os vícios de linguagem, as pausas e as hesitações para você. Uma única tomada basta.',
  'records.zeroState.title': 'Grave. Nós damos o acabamento.',
} satisfies RecordsTranslation;

const fr = {
  'records.credits.freeRecordingAvailable': '1 enregistrement gratuit · jusqu’à {minutes} min',
  'records.credits.freeRecordingUsed': 'Enregistrement gratuit utilisé',
  'records.credits.unlimited': 'Illimité',
  'records.newRecording': 'Nouvel enregistrement',
  'records.pageTitle': 'Enregistrer des vidéos',
  'records.preparingUpdatedVideo': 'Préparation de la vidéo mise à jour…',
  'records.status.aborted': 'Interrompu',
  'records.status.completed': 'Terminé',
  'records.status.created': 'Créé',
  'records.status.failed': 'Échec',
  'records.status.processing': 'Traitement en cours',
  'records.translate': 'Traduire',
  'records.translateAria': 'Traduire {name}',
  'records.type': 'Enregistrement d’écran',
  'records.zeroState.action': 'Enregistrer une vidéo',
  'records.zeroState.description':
    'Lancez l’enregistrement et parlez : nous supprimons les mots parasites, les pauses et les hésitations. Une seule prise suffit.',
  'records.zeroState.title': 'Enregistrez. On s’occupe du montage.',
} satisfies RecordsTranslation;

const it = {
  'records.credits.freeRecordingAvailable': '1 registrazione gratuita · fino a {minutes} min',
  'records.credits.freeRecordingUsed': 'Registrazione gratuita utilizzata',
  'records.credits.unlimited': 'Illimitato',
  'records.newRecording': 'Nuova registrazione',
  'records.pageTitle': 'Registra video',
  'records.preparingUpdatedVideo': 'Preparazione del video aggiornato…',
  'records.status.aborted': 'Interrotta',
  'records.status.completed': 'Completata',
  'records.status.created': 'Creata',
  'records.status.failed': 'Non riuscita',
  'records.status.processing': 'In elaborazione',
  'records.translate': 'Traduci',
  'records.translateAria': 'Traduci {name}',
  'records.type': 'Registrazione dello schermo',
  'records.zeroState.action': 'Registra video',
  'records.zeroState.description':
    'Avvia la registrazione e parla: tagliamo noi intercalari, pause ed esitazioni. Basta una sola ripresa.',
  'records.zeroState.title': 'Registra. Al resto pensiamo noi.',
} satisfies RecordsTranslation;

const ru = {
  'records.credits.freeRecordingAvailable': '1 бесплатная запись · до {minutes} мин',
  'records.credits.freeRecordingUsed': 'Бесплатная запись уже использована',
  'records.credits.unlimited': 'Без ограничений',
  'records.newRecording': 'Новая запись',
  'records.pageTitle': 'Запись видео',
  'records.preparingUpdatedVideo': 'Готовим обновлённое видео…',
  'records.status.aborted': 'Прервана',
  'records.status.completed': 'Готова',
  'records.status.created': 'Создана',
  'records.status.failed': 'Ошибка',
  'records.status.processing': 'Обработка',
  'records.translate': 'Перевести',
  'records.translateAria': 'Перевести «{name}»',
  'records.type': 'Запись экрана',
  'records.zeroState.action': 'Записать видео',
  'records.zeroState.description':
    'Просто включите запись и говорите — мы уберём слова-паразиты, паузы и запинки. Хватит одного дубля.',
  'records.zeroState.title': 'Записывайте — мы всё подчистим.',
} satisfies RecordsTranslation;

export const RECORDS_TRANSLATIONS = defineDomainTranslations({ en, es, fr, it, pt, ru });
